import { useCallback, useEffect, useRef, useState } from 'react';
import { trpc } from '@org/clients/school';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type { ExerciseFeedbackMessage } from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../shared/exercise-feedback-list';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import {
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Loader2,
  MessageCircle,
  Sparkles,
  Volume2,
} from 'lucide-react';
import { Button } from '../../../core/button';
import { Badge } from '../../../core/badge';
import { Alert, AlertDescription } from '../../../core/alert';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../../core/tooltip';
import {
  useElevenLabsConversation,
  type ElevenLabsMessage,
  type ElevenLabsMode,
} from '../../hooks/use-elevenlabs-conversation';

interface ElevenLabsConversationUIProps {
  content: StudentExerciseContent & {
    theme?: string;
    maxDurationSec?: number;
    systemPrompt?: string;
    targetSkills?: string[];
  };
  session: {
    sessionId: string;
    ephemeralKey: string;
    elevenlabsContext?: string;
  };
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  onEnd: () => void;
  onTurnSaved?: (role: 'user' | 'assistant', content: string) => void;
}

interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

type ConversationState = 'connecting' | 'active' | 'ending';

/**
 * ElevenLabs Conversational AI UI component.
 * Uses VAD (Voice Activity Detection) instead of push-to-talk.
 * The AI automatically detects when the user is speaking and responds.
 */
export function ElevenLabsConversationUI({
  content,
  session,
  feedback,
  variant = 'card',
  onEnd,
  onTurnSaved,
}: ElevenLabsConversationUIProps) {
  const [conversationState, setConversationState] = useState<ConversationState>('connecting');
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(content.maxDurationSec ?? 300);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const turnsContainerRef = useRef<HTMLDivElement>(null);
  const isEndingRef = useRef(false);

  // tRPC mutation for saving turns
  const saveTurnMutation = trpc.conversation.saveTurn.useMutation({
    onError: (error) => {
      console.error('Failed to save turn:', error);
      // Don't show error to user for turn saves - conversation continues
    },
  });

  // Handle incoming messages
  const handleMessage = useCallback(
    (message: ElevenLabsMessage) => {
      const role = message.source === 'user' ? 'user' : 'assistant';
      const turn: ConversationTurn = {
        role,
        content: message.message,
        timestamp: new Date(),
      };
      setTurns((prev) => [...prev, turn]);

      // Save turn to database
      saveTurnMutation.mutate({
        sessionId: session.sessionId,
        role,
        content: message.message,
      });

      onTurnSaved?.(role, message.message);
    },
    [session.sessionId, saveTurnMutation, onTurnSaved]
  );

  // Handle mode changes for UI feedback
  const handleModeChange = useCallback((mode: ElevenLabsMode) => {
    // Mode is either 'listening' or 'speaking'
    // We can use this for visual feedback
  }, []);

  // Handle errors
  const handleError = useCallback((err: Error) => {
    console.error('ElevenLabs error:', err);
    setError(err.message);
  }, []);

  // Handle disconnection
  const handleDisconnect = useCallback(() => {
    if (conversationState !== 'ending') {
      // Unexpected disconnect
      setError('Connection lost. Please try again.');
    }
  }, [conversationState]);

  // Initialize the ElevenLabs conversation hook
  const {
    status,
    mode,
    isMuted,
    connect,
    disconnect,
    toggleMute,
  } = useElevenLabsConversation({
    signedUrl: session.ephemeralKey,
    context: session.elevenlabsContext,
    onMessage: handleMessage,
    onModeChange: handleModeChange,
    onError: handleError,
    onDisconnect: handleDisconnect,
  });

  const handleEndConversation = useCallback(async () => {
    // Prevent concurrent execution
    if (isEndingRef.current) {
      return;
    }
    isEndingRef.current = true;

    setConversationState('ending');
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      await disconnect();
    } catch (error) {
      console.error('Error disconnecting:', error);
    }

    onEnd();
  }, [disconnect, onEnd]);

  // Connect on mount
  useEffect(() => {
    connect();

    return () => {
      // Cleanup on unmount
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [connect]);

  // Handle status changes
  useEffect(() => {
    if (status === 'connected') {
      setConversationState('active');

      // Clear any existing timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Start countdown timer
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleEndConversation();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Cleanup timer when status changes or component unmounts
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status, handleEndConversation]);

  // Auto-scroll to bottom when new turns are added
  useEffect(() => {
    if (turnsContainerRef.current) {
      turnsContainerRef.current.scrollTop = turnsContainerRef.current.scrollHeight;
    }
  }, [turns]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isListening = mode === 'listening';
  const isSpeaking = mode === 'speaking';
  const isActive = isSpeaking || isListening;

  return (
    <div
      className={cn(
        'space-y-6',
        variant === 'card' &&
          'rounded-[24px] border border-rose-100 bg-white/90 p-6 shadow-sm backdrop-blur'
      )}
    >
      {/* Header with title */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {content.title && (
            <h3 className="text-xl font-semibold text-gray-900">
              {renderInlineMarkdown(content.title, 'conversational-exercise-title')}
            </h3>
          )}
        </div>
        <Badge
          variant="secondary"
          className="gap-1 px-2 py-1 bg-emerald-50 border-emerald-200 text-emerald-700"
        >
          <Sparkles className="h-3 w-3" />
          ElevenLabs
        </Badge>
      </div>
      <ContentDescription text={content.description} markdown />

      {content.theme && (
        <Badge
          variant="secondary"
          className="gap-2 px-4 py-2 bg-gradient-to-r from-rose-50 to-pink-50 border-rose-100 text-gray-700"
        >
          <MessageCircle className="h-4 w-4 text-[#cf2e2e]" />
          <span>
            <span className="font-semibold text-[#cf2e2e]">Theme:</span> {content.theme}
          </span>
        </Badge>
      )}

      {error && (
        <Alert variant="destructive" className="rounded-2xl">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Connecting state */}
      {conversationState === 'connecting' && (
        <div className="flex flex-col items-center space-y-6 py-12">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#cf2e2e] to-[#f78da7] opacity-30 blur-xl animate-pulse" />
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#cf2e2e] via-[#f05f7a] to-[#f78da7]">
              <Loader2 className="h-10 w-10 animate-spin text-white" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-700">Connecting...</p>
            <p className="text-xs text-gray-500 mt-1">Setting up your conversation</p>
          </div>
        </div>
      )}

      {/* Active conversation */}
      {(conversationState === 'active' || conversationState === 'ending') && (
        <div className="flex flex-col min-h-[400px]">
          {/* Conversation transcript */}
          <div
            ref={turnsContainerRef}
            className={cn(
              'flex-1 space-y-3 overflow-y-auto rounded-t-[24px] p-4 sm:p-5 transition-colors duration-300',
              isSpeaking
                ? 'bg-gradient-to-b from-emerald-50/50 to-emerald-100/30'
                : isListening
                ? 'bg-gradient-to-b from-rose-50/50 to-rose-100/30'
                : 'bg-gradient-to-b from-slate-50 to-white'
            )}
            style={{ maxHeight: '280px', minHeight: '120px' }}
          >
            {turns.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-6 text-center">
                <p className="text-sm text-slate-400">
                  {isSpeaking
                    ? 'The tutor is speaking...'
                    : 'Start speaking - the AI will respond automatically'}
                </p>
              </div>
            ) : (
              turns.map((turn, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex animate-in fade-in slide-in-from-bottom-2 duration-300',
                    turn.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[85%] rounded-2xl px-4 py-2.5',
                      turn.role === 'user'
                        ? 'bg-slate-800 text-white'
                        : 'bg-white border border-slate-200 text-slate-700 shadow-sm'
                    )}
                  >
                    <p className="text-sm leading-relaxed">{turn.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* VAD Control Zone */}
          <div
            className={cn(
              'rounded-b-[24px] px-4 py-4 sm:py-6 transition-all duration-300',
              isSpeaking
                ? 'bg-gradient-to-t from-emerald-500 via-emerald-400 to-emerald-300'
                : isListening
                ? 'bg-gradient-to-t from-rose-500 via-rose-400 to-rose-300'
                : 'bg-gradient-to-t from-slate-100 via-slate-50 to-white border-t border-slate-100'
            )}
          >
            {/* Top row: Timer and controls */}
            <div className="flex items-center justify-between mb-4">
              {/* Timer badge */}
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-200/80 text-slate-600'
                )}
              >
                <div
                  className={cn(
                    'w-1.5 h-1.5 rounded-full shrink-0',
                    isActive ? 'bg-white animate-pulse' : 'bg-emerald-500'
                  )}
                />
                <span className="font-mono tracking-wide">{formatTime(timeRemaining)}</span>
              </div>

              {/* Mute and End buttons */}
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={toggleMute}
                      disabled={conversationState === 'ending'}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium h-auto shrink-0',
                        isActive
                          ? 'bg-white/20 text-white hover:bg-white/30'
                          : 'bg-slate-200/80 text-slate-600 hover:bg-slate-300/80'
                      )}
                    >
                      {isMuted ? (
                        <MicOff className="h-3.5 w-3.5" />
                      ) : (
                        <Mic className="h-3.5 w-3.5" />
                      )}
                      <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isMuted ? 'Unmute microphone' : 'Mute microphone'}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleEndConversation}
                      disabled={conversationState === 'ending'}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium h-auto shrink-0',
                        isActive
                          ? 'bg-white/20 text-white/80 hover:bg-white/30'
                          : 'bg-slate-200/80 text-slate-600 hover:bg-slate-300/80'
                      )}
                    >
                      <PhoneOff className="h-3.5 w-3.5" />
                      <span>End</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {conversationState === 'ending' ? 'Ending...' : 'End conversation'}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Large central status indicator */}
            <div className="flex justify-center mb-3">
              <div
                className={cn(
                  'relative flex items-center justify-center rounded-full transition-all duration-300',
                  isActive ? 'h-20 w-20 bg-white shadow-lg' : 'h-16 w-16 bg-slate-200'
                )}
              >
                {/* Pulsing rings when active */}
                {isActive && (
                  <>
                    <span
                      className={cn(
                        'absolute inset-0 rounded-full animate-ping',
                        isSpeaking ? 'bg-emerald-400/40' : 'bg-rose-400/40'
                      )}
                    />
                    <span className="absolute -inset-2 rounded-full border-2 animate-pulse border-white/50" />
                  </>
                )}

                {isSpeaking ? (
                  <Volume2
                    className={cn(
                      'relative z-10 h-8 w-8 text-emerald-600 animate-pulse'
                    )}
                  />
                ) : isListening ? (
                  <Mic
                    className={cn(
                      'relative z-10 h-8 w-8 text-rose-600',
                      !isMuted && 'animate-pulse'
                    )}
                  />
                ) : (
                  <Phone className="relative z-10 h-6 w-6 text-slate-400" />
                )}
              </div>
            </div>

            {/* Status text */}
            <div
              className={cn(
                'text-center h-5 transition-colors duration-200',
                isActive ? 'text-white/90' : 'text-slate-500'
              )}
            >
              {conversationState === 'ending' ? (
                <span className="text-sm font-medium">Ending conversation...</span>
              ) : isSpeaking ? (
                <span className="text-sm font-medium animate-pulse">Tutor is speaking...</span>
              ) : isListening ? (
                <span className="text-sm font-medium">
                  {isMuted ? 'Microphone muted' : 'Listening... speak when ready'}
                </span>
              ) : (
                <span className="text-sm">Waiting...</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show feedback if any */}
      {feedback && feedback.length > 0 && <ExerciseFeedbackList feedback={feedback} />}
    </div>
  );
}
